import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AuthState,
  AuthUser,
  AuthCompany,
  LoginResponse,
  RefreshResponse,
} from '../modules/auth/interfaces';

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  rememberMe: false,
  companies: [],
  selectedCompanyId: null,
  isLoading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Imposta stato auth dopo login.
     */
    setAuth: (state, action: PayloadAction<LoginResponse & { rememberMe?: boolean }>) => {
      const { accessToken, refreshToken, user, companies, rememberMe } = action.payload;
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.user = user;
      state.rememberMe = rememberMe ?? false;
      state.companies = companies || [];
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;

      // Auto-select company se una sola E requiresCompanySelection è false
      // Se requiresCompanySelection è true, l'utente deve selezionare manualmente
      if (state.companies.length === 1 && !user?.requiresCompanySelection) {
        state.selectedCompanyId = state.companies[0].companyId;
      }
    },

    /**
     * Aggiorna tokens dopo refresh.
     */
    updateTokens: (state, action: PayloadAction<RefreshResponse>) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },

    /**
     * Seleziona company corrente.
     */
    selectCompany: (state, action: PayloadAction<string>) => {
      state.selectedCompanyId = action.payload;
    },

    /**
     * Imposta stato loading.
     */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    /**
     * Imposta errore.
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Reset stato auth (logout).
     */
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.rememberMe = false;
      state.companies = [];
      state.selectedCompanyId = null;
      state.isLoading = false;
      state.error = null;
    },
  },
});

export const {
  setAuth,
  updateTokens,
  selectCompany,
  setLoading,
  setError,
  clearAuth,
} = authSlice.actions;

export default authSlice.reducer;
